import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import courses from "../data/courses";
import CourseCard from "../components/CourseCard";

import {
  FaSearch,
  FaTimes,
  FaFilter,
} from "react-icons/fa";

import {
  HiArrowRight,
  HiDocumentText,
  HiUserGroup,
  HiBuildingOffice2,
  HiAcademicCap,
  HiUser,
  HiOutlineSparkles,
} from "react-icons/hi2";

import { Link } from "react-router-dom";

const categories = [
  "All",
  "Programming",
  "Data Science",
  "Data Analytics",
  "Web Development",
  "AI/ML",
  "Productivity",
  "Marketing",
  "QA/Automation",
  "Electronics",
  "Design",
  "ERP",
];

const stats = [
  {
    value: 5000,
    label: "Students Trained",
    icon: HiUserGroup,
  },
  {
    value: 50,
    label: "Companies Partnered",
    icon: HiBuildingOffice2,
  },
  {
    value: 5000,
    label: "Certifications Awarded",
    icon: HiAcademicCap,
  },
  {
    value: 20,
    label: "Expert Trainers",
    icon: HiUser,
  },
];

const Courses = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      const matchesCategory =
        activeCategory === "All" ||
        course.category === activeCategory;

      const searchableText = [
        course.title,
        course.category,
        course.description,
        course.level,
        course.label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch ||
        searchableText.includes(normalizedSearch);

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, normalizedSearch]);

  const clearFilters = () => {
    setSearchTerm("");
    setActiveCategory("All");
  };

  return (
    <main className="relative overflow-hidden bg-white dark:bg-gray-950">

      {/* Subtle background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[320px] overflow-hidden"
      >
        <div
          className="
        absolute left-1/2 top-[-140px]
        h-[320px] w-[620px]
        -translate-x-1/2
        rounded-full
        bg-blue-500/[0.05]
        blur-3xl
      "
        />
      </div>

      {/* Page Content */}
      <div
        className="
      relative z-10
      mx-auto max-w-7xl
      px-4
      pb-10
      pt-24
      sm:px-6
      sm:pt-28
      lg:px-8
      lg:pt-30
    "
      >

        {/* HERO */}
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mx-auto max-w-4xl text-center"
        >

          {/* Eyebrow */}
          <span
            className="
          inline-flex items-center gap-1.5
          rounded-full
          border border-blue-200
          bg-blue-50
          px-3 py-1
          text-[10px] font-semibold
          uppercase tracking-[0.12em]
          text-blue-700
          dark:border-blue-900/60
          dark:bg-blue-950/40
          dark:text-blue-300
        "
          >
            <HiOutlineSparkles className="text-xs" />
            Career-Focused Learning
          </span>

          {/* H1 */}
          <h1
            className="
    mx-auto mt-4
    max-w-6xl
    text-[2.2rem]
    font-bold
    leading-[1.1]
    tracking-[-0.03em]
    text-slate-900
    sm:text-4xl
    lg:text-[3.25rem]
    dark:text-white
  "
          >
            <span className="block">
              Software Courses{" "}
              <span
                className="
        bg-gradient-to-r
        from-blue-600
        to-indigo-600
        bg-clip-text
        text-transparent
        dark:from-blue-400
        dark:to-indigo-400
      "
              >
                &amp; Professional Training
              </span>
            </span>

            <span
              className="
      mt-1 block
      text-[1.8rem]
      font-semibold
      tracking-[-0.02em]
      text-slate-700
      sm:text-3xl
      lg:text-[2.75rem]
      dark:text-slate-200
    "
            >
              in Tirupati
            </span>
          </h1>
          
          {/* Description */}
          <p
            className="
          mx-auto mt-4
          max-w-2xl
          text-sm
          leading-6
          text-slate-600
          sm:text-[15px]
          dark:text-slate-300
        "
          >
            Explore practical courses in programming, data analytics,
            data science, web development, AI, digital marketing,
            networking, and other in-demand technology skills.
          </p>

        </motion.header>

        {/* =========================================================
            SEARCH + FILTER PANEL
        ========================================================== */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.45 }}
          aria-label="Course search and filters"
          className="
            mx-auto mt-10 max-w-6xl
            rounded-[24px]
            border border-gray-200/80
            bg-white/90
            p-4
            shadow-[0_10px_40px_rgba(15,23,42,0.06)]
            backdrop-blur-xl
            sm:p-5
            dark:border-gray-800
            dark:bg-gray-900/90
          "
        >
          {/* Search */}
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-xl">
              <label htmlFor="course-search" className="sr-only">
                Search courses
              </label>

              <FaSearch
                aria-hidden="true"
                className="
                  pointer-events-none
                  absolute left-4 top-1/2
                  -translate-y-1/2
                  text-sm text-gray-400
                "
              />

              <input
                id="course-search"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by course, skill or category..."
                className="
                  h-12 w-full
                  rounded-xl
                  border border-gray-200
                  bg-gray-50
                  pl-11 pr-11
                  text-sm
                  text-gray-900
                  outline-none
                  transition-all
                  placeholder:text-gray-400
                  focus:border-blue-500
                  focus:bg-white
                  focus:ring-4
                  focus:ring-blue-500/10
                  dark:border-gray-700
                  dark:bg-gray-800
                  dark:text-white
                  dark:placeholder:text-gray-500
                  dark:focus:border-blue-400
                  dark:focus:bg-gray-800
                "
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  className="
                    absolute right-3 top-1/2
                    flex h-7 w-7
                    -translate-y-1/2
                    items-center justify-center
                    rounded-full
                    text-gray-400
                    transition-colors
                    hover:bg-gray-200
                    hover:text-gray-700
                    dark:hover:bg-gray-700
                    dark:hover:text-gray-200
                  "
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>

            {/* Result count */}
            <div
              className="
                flex items-center justify-between
                gap-4
                text-xs
                text-gray-500
                sm:text-sm
                dark:text-gray-400
              "
            >
              <span className="inline-flex items-center gap-2">
                <FaFilter className="text-[11px]" />
                Showing{" "}
                <strong className="text-gray-800 dark:text-gray-200">
                  {filteredCourses.length}
                </strong>{" "}
                courses
              </span>

              {(activeCategory !== "All" || searchTerm) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="
                    font-semibold
                    text-blue-600
                    transition-colors
                    hover:text-blue-800
                    dark:text-blue-400
                    dark:hover:text-blue-300
                  "
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Category Navigation */}
          <div className="mt-5 border-t border-gray-100 pt-4 dark:border-gray-800">
            <div
              className="
                flex gap-2
                overflow-x-auto
                pb-1
                scrollbar-hide
              "
              role="tablist"
              aria-label="Course categories"
            >
              {categories.map((category) => {
                const isActive = activeCategory === category;

                return (
                  <button
                    key={category}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveCategory(category)}
                    className={`
                      shrink-0
                      rounded-full
                      border
                      px-4 py-2
                      text-xs font-semibold
                      transition-all duration-200
                      ${isActive
                        ? "border-blue-600 bg-blue-600 text-white shadow-md shadow-blue-600/20"
                        : "border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-blue-700 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
                      }
                    `}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* =========================================================
            COURSE GRID
        ========================================================== */}
        <section
          aria-labelledby="course-grid-heading"
          className="mt-10"
        >
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p
                className="
                  text-[11px] font-bold
                  uppercase tracking-[0.14em]
                  text-blue-600
                  dark:text-blue-400
                "
              >
                Explore Learning Paths
              </p>

              <h2
                id="course-grid-heading"
                className="
                  mt-1
                  text-2xl font-bold tracking-tight
                  text-gray-900
                  sm:text-3xl
                  dark:text-white
                "
              >
                Find the Right Course for Your Goals
              </h2>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              {activeCategory === "All"
                ? "All available courses"
                : `${activeCategory} courses`}
            </p>
          </div>

          {filteredCourses.length > 0 ? (
            <div
              className="
                grid
                grid-cols-1
                gap-5
                sm:grid-cols-2
                lg:grid-cols-3
                xl:grid-cols-4
              "
            >
              {filteredCourses.map((course, index) => (
                <motion.div
                  key={course.slug}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.08 }}
                  transition={{
                    duration: 0.4,
                    delay: Math.min(index * 0.04, 0.2),
                  }}
                >
                  <CourseCard
                    title={course.title}
                    slug={course.slug}
                    icon={course.icon}
                    category={course.category}
                    description={course.description}
                    rating={course.rating}
                    students={course.students}
                    level={course.level}
                    label={course.label}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div
              className="
                rounded-[24px]
                border border-dashed
                border-gray-300
                bg-gray-50/80
                px-6 py-16
                text-center
                dark:border-gray-700
                dark:bg-gray-900/60
              "
            >
              <div
                className="
                  mx-auto flex h-14 w-14
                  items-center justify-center
                  rounded-2xl
                  bg-white
                  text-gray-400
                  shadow-sm
                  dark:bg-gray-800
                "
              >
                <FaSearch />
              </div>

              <h3
                className="
                  mt-4
                  text-lg font-semibold
                  text-gray-900
                  dark:text-white
                "
              >
                No courses found
              </h3>

              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-500 dark:text-gray-400">
                Try a different search term or choose another course
                category.
              </p>

              <button
                type="button"
                onClick={clearFilters}
                className="
                  mt-5
                  inline-flex items-center
                  rounded-xl
                  bg-blue-600
                  px-5 py-2.5
                  text-sm font-semibold
                  text-white
                  shadow-md
                  transition-all
                  hover:bg-blue-700
                  hover:shadow-lg
                "
              >
                View All Courses
              </button>
            </div>
          )}
        </section>

        {/* =========================================================
            CTA
        ========================================================== */}
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="
            relative mt-14
            overflow-hidden
            rounded-[28px]
            bg-gradient-to-br
            from-blue-700
            via-indigo-700
            to-purple-700
            px-6 py-9
            shadow-[0_18px_50px_rgba(37,99,235,0.18)]
            sm:px-10
            sm:py-10
          "
        >
          {/* Decorative elements */}
          <div
            aria-hidden="true"
            className="
              absolute -right-16 -top-16
              h-52 w-52
              rounded-full
              border border-white/10
            "
          />

          <div
            aria-hidden="true"
            className="
              absolute -bottom-20 -left-10
              h-48 w-48
              rounded-full
              bg-white/5
              blur-2xl
            "
          />

          <div className="relative z-10 flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl text-white">
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-100">
                Ready to Start?
              </span>

              <h2 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
                Build Skills That Move Your Career Forward
              </h2>

              <p className="mt-3 max-w-xl text-sm leading-6 text-blue-100 sm:text-base">
                Explore the available programs and connect with IA4Solutions
                to choose a learning path that fits your goals.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/register"
                className="
                  inline-flex items-center
                  justify-center gap-2
                  rounded-xl
                  bg-white
                  px-5 py-3
                  text-sm font-bold
                  text-blue-700
                  shadow-lg
                  transition-all duration-200
                  hover:-translate-y-0.5
                  hover:shadow-xl
                "
              >
                Enroll Now
                <HiArrowRight className="text-base" />
              </Link>

              <Link
                to="/register"
                className="
                  inline-flex items-center
                  justify-center gap-2
                  rounded-xl
                  border border-white/25
                  bg-white/10
                  px-5 py-3
                  text-sm font-semibold
                  text-white
                  backdrop-blur-sm
                  transition-all duration-200
                  hover:bg-white/15
                "
              >
                <HiDocumentText className="text-base" />
                Get Course Information
              </Link>
            </div>
          </div>
        </motion.section>

        {/* =========================================================
            STATS
        ========================================================== */}
        <section
          aria-label="IA4Solutions achievements"
          className="mt-10"
        >
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {stats.map((stat, index) => {
              const StatIcon = stat.icon;

              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.08,
                  }}
                  className="
                    group
                    rounded-2xl
                    border border-gray-200/80
                    bg-white
                    px-4 py-5
                    text-center
                    shadow-[0_6px_24px_rgba(15,23,42,0.045)]
                    transition-all duration-300
                    hover:-translate-y-1
                    hover:shadow-[0_12px_30px_rgba(15,23,42,0.08)]
                    dark:border-gray-800
                    dark:bg-gray-900
                  "
                >
                  <div
                    className="
                      mx-auto flex h-10 w-10
                      items-center justify-center
                      rounded-xl
                      bg-blue-50
                      text-blue-600
                      transition-transform duration-300
                      group-hover:scale-105
                      dark:bg-blue-950/40
                      dark:text-blue-400
                    "
                  >
                    <StatIcon className="text-lg" />
                  </div>

                  <h3
                    className="
                      mt-3
                      text-xl font-bold
                      text-gray-900
                      sm:text-2xl
                      dark:text-white
                    "
                  >
                    <CountUp
                      start={0}
                      end={stat.value}
                      duration={2}
                      separator=","
                    />
                    +
                  </h3>

                  <p
                    className="
                      mt-1
                      text-[11px] font-medium
                      text-gray-500
                      sm:text-xs
                      dark:text-gray-400
                    "
                  >
                    {stat.label}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </section>
      </div>
    </main>
  );
};

export default Courses;